<script lang="ts">
    import {getInjector} from 'src/core';
    import type {ListValueEditorLocalizations, ListValueEditorOptions, ListValueEditorValidations} from './types';
    import type {Readable} from 'svelte/store';
    import type {ValueEditorsEventMap} from '../../../events';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControlOptions} from '../../forms/types';
    import type {Observable} from 'rxjs';
    import type {FormArray} from '../../forms/form-array';
    import type {ValueEditorSize, ValueEditorTypeOrAlias} from '../../../types';
    import {createEventDispatcher, getContext, onMount, setContext, tick} from 'svelte';
    import {
        ValueEditorConfigurationManagerProvider
    } from '../../../configuration/value-editor-configuration-manager.provider';
    import {FormArrayBuilder} from '../../forms/form-array';
    import {get} from 'svelte/store';
    import {uuid as generateUuid} from '../../../../utils/custom-utils';
    import {validatorFactory} from './utils';
    import {map} from 'rxjs';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {createInputDescription} from '../../description/utils';
    import {FORM_STRUCTURE_CHANGE} from '../../../events';
    import {EDITORS_LOCALIZE, FORM_BUILDER, UNIVERSAL_FORM_MODEL} from '../../../context-keys';
    import {cloneDeep} from 'lodash-es';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import KpLoadingInline from '../../../../components/kp-loading/KpLoadingInline.svelte';
    import ListValueEditorField from './ListValueEditorField.svelte';
    import Description from '../../description/Description.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    type MODEL_VALUE = $$Generic;

    export let model: MODEL_VALUE[];
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;

    export let placeholder: string;
    export let isDisabled: boolean;
    export let isVisible: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: ListValueEditorOptions;
    export let validations: ListValueEditorValidations;

    const WAITING_FOR_SHOW_SPINNER_MS = 100;

    const dispatch = createEventDispatcher<ValueEditorsEventMap<MODEL_VALUE[]>>();
    const configService = getInjector().getByClass(ValueEditorConfigurationManagerProvider);
    const localize: EditorsLocalizationFunction<ListValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const universalFormModel$: Readable<Record<string, unknown>> = getContext(UNIVERSAL_FORM_MODEL);
    const labelContext = getValueEditorLabelContext();

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const formArrayBuilder = new FormArrayBuilder<MODEL_VALUE>();
    formArrayBuilder.addValidators(validatorFactory(validations));

    setContext(FORM_BUILDER, formArrayBuilder);
    let formArray: FormArray<MODEL_VALUE>;

    let listSpecificErrors$: Observable<string[]>;
    let hasAnyListSpecificErrors$: Observable<boolean>;

    onMount(() => {
        const nameMap = new Map<string, number>();
        ids.forEach((id, index) => nameMap.set(resolveProperty(id, 'editorName'), index));
        formArray = formArrayBuilder.build(editorId, editorName, options.emptyAsNull, nameMap);
        listSpecificErrors$ = formArray.getErrors$().pipe(map(filterOutNestedErrorMessages));
        hasAnyListSpecificErrors$ = listSpecificErrors$.pipe(map((errors: string[]) => errors.length > 0));
    });

    let ids: string[]; // array of ids corresponding to modelValues in model (these arrays need to be in sync)
    model = normalizeModelIfNeeded(model);
    if (validations?.required && (model?.length ?? 0) < 1 && options.autoCreateNewItemIfRequired) {
        model = [options.newItemPrototype];
    }
    ids = model?.map(() => generateUuid()) ?? [];

    $: {
        model = normalizeModelIfNeeded(model);
    }

    let canRemoveItems: boolean;
    $: canRemoveItems = !!model && ((model.length > 0 && !validations?.required) || (model.length > 1 && validations?.required));
    let canAddItem: boolean;
    $: canAddItem = validations?.maxCount ? (model?.length ?? 0) < validations?.maxCount : true;

    let waitingForPrototype = false;

    function normalizeModelIfNeeded(modelValue: typeof model) {
        if (!Array.isArray(modelValue)) {
            if (options.emptyAsNull) {
                modelValue = null;
            } else {
                modelValue = [];
            }
            ids = [];
        } else {
            if (options.emptyAsNull && modelValue.length === 0) {
                modelValue = null;
                ids = [];
            }
        }
        return modelValue;
    }

    async function addItem() {
        if (!canAddItem) {
            return;
        }

        let prototype;

        if (typeof options.onAddItem === 'function') {
            const showSpinnerTimeoutPromise = setTimeout(() => waitingForPrototype = true, WAITING_FOR_SHOW_SPINNER_MS);

            try {
                prototype = await options.onAddItem({
                    model,
                    propertyName: editorName,
                    formModel: options.sendWholeForm ? cloneDeep(get(universalFormModel$)) : undefined,
                    additionalParameters: options.additionalParameters
                });
            } finally {
                clearTimeout(showSpinnerTimeoutPromise);
                waitingForPrototype = false;
            }
        } else {
            prototype = options.newItemPrototype;
        }
        model = [...model, cloneDeep(prototype)]; // add new item to model causing DOM update (adding new value editor)
        ids = [...ids, generateUuid()];
        await tick(); // wait for next DOM update (new editor will register itself to the form builder on mount)
        const newFormControl = formArrayBuilder.getLastRegisteredFormController(); // newly created form control from added editor
        formArray.setTouched(); // to display errors
        formArray.addFieldAt(model.length - 1, newFormControl); // append new form control to form group
        await tick(); // wait for new editor initial validation (until model value stabilizes)
        dispatch(FORM_STRUCTURE_CHANGE);
        formArray.triggerInputEvent();
    }

    async function removeItem(index: number) {
        if (!canRemoveItems) {
            return;
        }
        if (model.length === 1 && options.emptyAsNull) {
            model = [];
            ids = [];
        } else {
            model.splice(index, 1);
            ids.splice(index, 1);
            model = model; // to trigger svelte reactivity
            ids = ids; // to trigger svelte reactivity
        }
        formArray.setTouched(); // to display errors
        formArray.removeFieldAt(index);
        await tick(); // wait for form array to recompose, used mainly for tests (event is fired after the changes)
        dispatch(FORM_STRUCTURE_CHANGE);
        formArray.triggerInputEvent();
    }

    function resolveProperty(uuid: string, property: 'editorId' | 'editorName'): string {
        const propertyValue = options.subEditor?.[property] ?? $$props[property];

        return `${propertyValue}_${uuid}`;
    }

    function resolveType(): ValueEditorTypeOrAlias {
        return options.subEditor?.type ?? configService.getManagerForType('list').getDefaults().subEditor.type;
    }

    function updateForm() {
        formArray.recomposeController();
        dispatch(FORM_STRUCTURE_CHANGE);
    }

    function filterOutNestedErrorMessages(errorMessages: string[]): string[] {
        // regex for testing nested errors - format fieldName.errorMessage e.g. someList_0d64f147-2c70-4d91-b670-74f942bfaa75.required
        const nestedErrorRegex = new RegExp(`^${editorName}_[a-fA-F0-9-_]+\\.`);
        return errorMessages.filter((errorMessage) => !nestedErrorRegex.test(errorMessage));
    }

    export function getFormController(): FormArray<MODEL_VALUE> {
        return formArray;
    }
</script>

<div class="panel panel-default list-value-editor-panel"
     class:has-list-specific-error={!!hasAnyListSpecificErrors$ && $hasAnyListSpecificErrors$}
     aria-invalid={!!hasAnyListSpecificErrors$ && $hasAnyListSpecificErrors$}
     id={editorId}
     data-name={editorName}
     use:ariaDescribedby={inputDescription}>

    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions div is not focusable so touch is only on click -->
    <div class="panel-body"
         role="group"
         on:click|once={() => formArray.setTouched()}
         use:ariaLabelledby={labelContext}>

        {#if (!model || model.length === 0) && placeholder}
            <div class="list-item placeholder">
                {placeholder}
            </div>
        {/if}

        {#each ids as id, index (id)}
            <div class="list-item">
                <ListValueEditorField
                        editorId={resolveProperty(id, 'editorId')}
                        editorName={resolveProperty(id, 'editorName')}
                        orderNumber={index + 1}
                        bind:model={model[index]}
                        formControlOptions={cloneDeep(formControlOptions)}

                        {...{placeholder: options.subEditor.placeholder}}
                        {...{type: resolveType()}}
                        isDisabled={options.subEditor?.isDisabled ?? isDisabled}
                        isVisible={options.subEditor?.isVisible ?? isVisible ?? true}
                        isFocused={isFocused && index === 0}
                        forceShowErrors={options.subEditor.forceShowErrors ?? forceShowErrors}
                        size={options.subEditor.size ?? size}
                        validations={options.subEditor.validations}
                        options={options.subEditor.options}
                        localizations={options.subEditor.localizations}

                        on:form-submit-request
                        on:form-structure-change={updateForm}/>

                <div class="list-item-delete-button">
                    {#if canRemoveItems}
                        <button class="btn btn-default btn-{size} remove"
                                type="button"
                                title={localize('remove')}
                                disabled={isDisabled}
                                on:click={() => removeItem(index)}>
                            <UIcon icon="trash"/>
                            <span class="sr-only">{localize('item')} {index + 1} - {localize('remove')}</span>
                        </button>
                    {/if}
                </div>
            </div>

            {#if /* not last */ index < model.length - 1}
                <hr class="list-item-divider"/>
            {/if}
        {/each}
    </div>

    <div class="panel-footer">
        {#if canAddItem}
            <button class="btn btn-default btn-block btn-xs add"
                    type="button"
                    disabled={isDisabled || waitingForPrototype}
                    on:click={addItem}>

                <UIcon icon="add"/>
                <span class="btn-content">{localize('add')}</span>

                {#if waitingForPrototype}
                    <KpLoadingInline size="xs"/>
                {/if}
            </button>
        {/if}
    </div>

    {#if formArray}
        <ErrorMessages formController={formArray}
                       errors={listSpecificErrors$} {forceShowErrors} {size}
                       customClass="list-editor-error"/>
    {/if}
</div>

<Description {inputDescription}/>

<style lang="less">
    @import "bootstrap-less/bootstrap/mixins";

    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .list-value-editor-panel {
        transition: border-color 0.15s ease-in-out;
        margin-bottom: 0;
    }

    :global(.has-error .list-value-editor-panel.has-list-specific-error) {
        border-color: var(--danger-red);
    }

    /* reset error styles if editor is nested in meta-editor which has error */
    :global(.has-not-error .has-error .list-value-editor-panel.has-list-specific-error) {
        border-color: @themed-border-default;
    }

    .panel-footer {
        padding: 0;

        .btn {
            border: 0;
            .border-top-radius(0);
        }
    }

    .btn.add {
        display: flex;
        justify-content: center;
        align-items: center;

        .btn-content {
            margin-left: 5px;
            margin-right: 5px;
        }
    }

    .placeholder {
        justify-content: center;
        color: @themed-text-muted;
    }

    .list-item {
        display: flex;
        flex-direction: row;
    }

    .list-item-delete-button {
        margin-left: @spacing-ml;
    }

    :global(kp-value-editor[data-type="list"] .error-message.list-editor-error) {
        margin-top: 1px;
    }
</style>
